import React from "react";
import { withTranslation, type WithTranslation } from "react-i18next";
import { useNavigate, type NavigateFunction } from "react-router-dom";
import {
  AlertTriangle,
  Bot,
  Check,
  Code2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Columns2,
  Copy,
  CopyPlus,
  Link2,
  LayoutGrid,
  List,
  Plus,
  Search,
  Share2,
  Trash2,
} from "lucide-react";
import { VIETMAP_STYLE_URLS } from "../config/vietmap-styles";
import {
  listCustomStyles,
  deleteCustomStyle,
  duplicateCustomStyle,
  hasNewerDraft,
  buildAbsoluteStyleUrl,
  type CustomStyleDto,
  type ThemeMode,
  type StylesApiError,
} from "../libs/styles-api";
import { matchesVi } from "../libs/layer-name";
import { toast } from "../libs/toast";
import { copyText } from "../libs/clipboard";
import { buildSnippets, fetchLatestGlJsVersion, GL_JS_FALLBACK_VERSION } from "../libs/embed-snippets";
import { buildAiPrompt } from "../libs/ai-prompt";
import type { ShareTab } from "./ShareEmbedModal";

// Has its own map — loaded the first time it opens.
const ShareEmbedModal = React.lazy(() => import("./ShareEmbedModal"));
import { DraftBadge } from "./maps/draft-badge";
import {
  getUser,
  isAuthConfigured,
  loadAccounts,
  loadConsumers,
  getConsumerId,
  getStoredApiKey,
  getAccountsLoadState,
  getAccountsLoadError,
  getConsumersLoadError,
  getConsumersLoadState,
  addAuthListener,
  getActiveAccountId,
} from "../libs/auth";
import { cn } from "@/lib/utils";
import { formatRelative, themeLabel } from "@/lib/relative-time";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { AppShell } from "@/components/layout/app-shell";
import { MapThumb } from "@/components/maps/map-thumb";
import { StyleTemplatesSection } from "@/components/maps/style-templates";
import { SignedOutLanding } from "@/components/maps/signed-out-landing";
import { PageHeader } from "@/components/page-header";

/**
 * Page size. Big enough to hold "all" a typical user's styles in one fetch so
 * the client-side search filter doesn't miss anything. Backend still paginates
 * if the user actually has more than 200 styles — but at that point the local
 * filter would be sluggish anyway and a backend keyword search would be better.
 */
const PAGE_SIZE = 200;

type ViewMode = "grid" | "list";

type ManagePageInternalProps = WithTranslation & {
  navigate: NavigateFunction
};

type ManagePageState = {
  page: number
  total: number
  totalPage: number
  keyword: string
  themeMode: ThemeMode | ""
  loading: boolean
  loadError: string | null
  items: CustomStyleDto[]
  busyId: string | null
  copiedId: string | null
  /** Style open in the share dialog, and the tab it opens on. */
  shareFor: CustomStyleDto | null
  shareTab: ShareTab
  /** Style đang chờ xác nhận xoá (mở ConfirmDialog). */
  pendingDelete: CustomStyleDto | null
  viewMode: ViewMode
};

// `themeMode` here is a thumbnail / display variant (NOT the ThemeMode enum
// stored on cloud styles). We use 0/1/2 to match the colors of saved-style
// cards plus 3 for Hybrid (satellite + labels).
const VECTOR_PRESETS: {
  id: string
  styleId: string
  name: string
  description: string
  themeMode: number
  badge: string
}[] = [
  {
    id: "default",
    styleId: "vietmap-street",
    name: "Default",
    description: "A bright and colorful style based on VietMapGL data.",
    themeMode: 0,
    badge: "Vector template",
  },
  {
    id: "light",
    styleId: "vietmap-light",
    name: "Light",
    description: "A bright and colorful style based on VietMapGL data.",
    themeMode: 1,
    badge: "Vector template",
  },
  {
    id: "dark",
    styleId: "vietmap-dark",
    name: "Dark",
    description: "A bright and colorful style based on VietMapGL data.",
    themeMode: 2,
    badge: "Vector template",
  },
  {
    id: "hybrid",
    styleId: "vietmap-hybrid",
    name: "Hybrid",
    description: "Satellite imagery base with road and POI labels on top.",
    themeMode: 3,
    badge: "Hybrid template",
  },
];

const GRID = "grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4";

class ManagePageInternal extends React.Component<
  ManagePageInternalProps,
  ManagePageState
> {
  private _authUnsubscribe: (() => void) | null = null;
  private _renderedKey = getStoredApiKey() ?? "";
  private _fetchInflight = false;
  /**
   * Last consumer id we successfully fetched against. When the auth listener
   * fires with a different consumer id, we must refetch even if a previous
   * call is still in-flight (the old one's response is for the wrong tenant).
   */
  /** Account (else consumer) the list was fetched for — styles belong to the account. */
  private _lastFetchedConsumer: string | null = null;

  /** The share dialog mounts on first open and then stays (close animation). */
  private _shareOpened = false;
  _shareModalMounted = () => (this._shareOpened ||= !!this.state.shareFor);

  state: ManagePageState = {
    page: 1,
    total: 0,
    totalPage: 0,
    keyword: "",
    themeMode: "",
    loading: false,
    loadError: null,
    items: [],
    busyId: null,
    copiedId: null,
    shareFor: null,
    shareTab: "url",
    pendingDelete: null,
    viewMode: "grid",
  };

  private _copyTimer: ReturnType<typeof setTimeout> | null = null;
  /** Newest stable Vietmap GL JS, resolved up front so copy stays synchronous-ish. */
  private _glJsVersion = GL_JS_FALLBACK_VERSION;

  componentDidMount() {
    fetchLatestGlJsVersion().then((v) => (this._glJsVersion = v));
    if (!isAuthConfigured() || !getUser()) return;
    this._authUnsubscribe = addAuthListener(() => {
      this._fetchWhenReady();
      // Thumbnails, templates and share links read the key in render.
      const key = getStoredApiKey() ?? "";
      if (key !== this._renderedKey) {
        this._renderedKey = key;
        this.forceUpdate();
      }
    });
    loadAccounts().then(() => loadConsumers());
    this._fetchWhenReady();
  }

  componentWillUnmount() {
    if (this._copyTimer) clearTimeout(this._copyTimer);
    this._authUnsubscribe?.();
    this._authUnsubscribe = null;
  }

  _scopeKey = (consumer: string) => getActiveAccountId() || consumer;

  _fetchWhenReady = () => {
    const consumer = getConsumerId();
    if (!consumer) {
      const accountsS = getAccountsLoadState();
      const consumersS = getConsumersLoadState();
      if (accountsS === "error") {
        this.setState({
          loading: false,
          loadError: [this.props.t("Không tải được tài khoản. Hãy F5."), getAccountsLoadError()].filter(Boolean).join(" "),
        });
      } else if (consumersS === "error") {
        this.setState({
          loading: false,
          loadError: [this.props.t("Không tải được consumer. Hãy F5."), getConsumersLoadError()].filter(Boolean).join(" "),
        });
      } else {
        this.setState({ loading: true, loadError: null });
      }
      // Reset so next consumer triggers a fresh fetch.
      this._lastFetchedConsumer = null;
      return;
    }
    // Account changed since the last fetch → must refetch even if inflight.
    // (Another consumer of the same account sees the same styles.)
    if (this._scopeKey(consumer) !== this._lastFetchedConsumer) {
      this._fetch(consumer);
      return;
    }
    // Same consumer already loaded (or loading): nothing to refetch — auth
    // events also fire for key / limits updates. Retry after an error.
    if (this._fetchInflight || this.state.loadError === null) return;
    this._fetch(consumer);
  };

  _fetch = async (consumerForCall?: string) => {
    const consumer = consumerForCall || getConsumerId();
    if (!consumer) return;
    this._fetchInflight = true;
    const consumerChanged =
      this._scopeKey(consumer) !== this._lastFetchedConsumer &&
      this._lastFetchedConsumer !== null;
    this._lastFetchedConsumer = this._scopeKey(consumer);
    if (consumerChanged) {
      // Tenant switched — reset to page 1.
      this.setState({ loading: true, loadError: null, page: 1 });
    } else {
      this.setState({ loading: true, loadError: null });
    }
    try {
      const { items, total, totalPage } = await listCustomStyles({
        page: this.state.page,
        size: PAGE_SIZE,
        // `keyword` is no longer sent — we filter the items array client-side
        // with VN-diacritic-aware matching so typing feels instant.
        themeMode: this.state.themeMode === "" ? undefined : this.state.themeMode,
      });
      // Late response: if consumer changed during the call, ignore — another
      // _fetch is in-flight or queued.
      if (getConsumerId() !== consumer) {
        this._fetchInflight = false;
        return;
      }
      this.setState({ items, total, totalPage, loading: false });
    } catch (err) {
      // Ignore errors from stale calls (consumer changed mid-flight).
      if (getConsumerId() !== consumer) {
        this._fetchInflight = false;
        return;
      }
      const apiErr = err as StylesApiError;
      this.setState({
        loading: false,
        loadError: apiErr.message || String(err),
        items: [],
        total: 0,
        totalPage: 0,
      });
    } finally {
      this._fetchInflight = false;
    }
  };

  _filteredItems = (): CustomStyleDto[] => {
    const q = this.state.keyword.trim();
    if (!q) return this.state.items;
    return this.state.items.filter(
      (s) =>
        matchesVi(s.name, q) ||
        (s.description ? matchesVi(s.description, q) : false) ||
        (s.tags || []).some((tag) => matchesVi(tag, q))
    );
  };

  _goToPage = (page: number) => {
    if (page < 1 || page > this.state.totalPage) return;
    this.setState({ page }, this._fetch);
  };

  _handleOpen = (item: CustomStyleDto) => {
    // Card click → detail page. Edit happens from there via the "Edit" CTA.
    this.props.navigate(`/detail/${encodeURIComponent(item.id)}`);
  };

  _handleOpenPreset = (presetId: string) => {
    // Open the editor with a preset style id passed via metadata. The editor
    // boots with its default style; the preset id is used by the Reset/Style
    // picker to fetch the right vietmap-* template.
    this.props.navigate(`/create?preset=${encodeURIComponent(presetId)}`);
  };

  _handleDuplicate = async (s: CustomStyleDto) => {
    const t = this.props.t;
    this.setState({ busyId: s.id });
    try {
      const copy = await duplicateCustomStyle(s.id);
      toast.success(t('Đã nhân bản thành "{{name}}"', { name: copy.name }));
      this.setState({ busyId: null }, () => this._fetch());
    } catch (err) {
      toast.error(t("Nhân bản thất bại: {{err}}", { err: (err as Error).message }));
      this.setState({ busyId: null });
    }
  };

  _confirmDelete = async () => {
    const item = this.state.pendingDelete;
    if (!item || this.state.busyId) return;
    const t = this.props.t;
    this.setState({ busyId: item.id });
    try {
      await deleteCustomStyle(item.id);
      toast.success(t('Đã xoá style "{{name}}"', { name: item.name }));
      const remaining = this.state.items.filter((s) => s.id !== item.id);
      this.setState(
        { items: remaining, busyId: null, pendingDelete: null },
        () => {
          if (remaining.length === 0 && this.state.page > 1) {
            this._goToPage(this.state.page - 1);
          } else {
            this._fetch();
          }
        }
      );
    } catch (err) {
      const apiErr = err as StylesApiError;
      const msg = apiErr.message || String(err);
      toast.error(t("Xoá thất bại: {{err}}", { err: msg }));
      this.setState({ busyId: null, pendingDelete: null, loadError: msg });
    }
  };

  _buildStyleUrl = (s: CustomStyleDto): string => {
    const apiKey = getStoredApiKey();
    const consumerId = getConsumerId();
    if (!apiKey || !consumerId) return "";
    return buildAbsoluteStyleUrl(s, apiKey, consumerId);
  };

  _handleCopy = async (s: CustomStyleDto, what: "url" | "code" | "ai") => {
    const t = this.props.t;
    const apiKey = getStoredApiKey() || "";
    const url = buildAbsoluteStyleUrl(s, apiKey, getConsumerId() || "");
    const text =
      what === "url" ? url
        : what === "code" ? buildSnippets(url, this._glJsVersion)[0].code
          : buildAiPrompt({
            name: s.name,
            theme: themeLabel(s.themeMode, t),
            description: s.description,
            styleUrl: url,
            includesKey: !!apiKey,
            glJsVersion: this._glJsVersion,
            lang: this.props.i18n.language?.startsWith("en") ? "en" : "vi",
          });
    await copyText(text);
    toast.success(
      what === "url" ? t("Đã sao chép style URL")
        : what === "code" ? t("Đã sao chép code (Web)")
          : t("Đã sao chép prompt AI")
    );
    this.setState({ copiedId: s.id });
    if (this._copyTimer) clearTimeout(this._copyTimer);
    this._copyTimer = setTimeout(() => this.setState({ copiedId: null }), 1500);
  };

  _onCardKey = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      action();
    }
  };

  _renderRowActions(s: CustomStyleDto) {
    const t = this.props.t;
    const isBusy = this.state.busyId === s.id;
    const copied = this.state.copiedId === s.id;
    return (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  aria-label={t("Sao chép & chia sẻ")}
                  disabled={isBusy}
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>{t("Sao chép & chia sẻ")}</TooltipContent>
          </Tooltip>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onSelect={() => this._handleCopy(s, "url")}>
              <Link2 />
              {t("Sao chép style URL")}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => this._handleCopy(s, "code")}>
              <Code2 />
              {t("Sao chép code (Web)")}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => this._handleCopy(s, "ai")}>
              <Bot />
              {t("Sao chép prompt AI")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => this.setState({ shareFor: s, shareTab: "code" })}>
              <Share2 />
              {t("Chia sẻ & nhúng…")}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => this._handleDuplicate(s)}>
              <CopyPlus />
              {t("Nhân bản")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="text-destructive hover:text-destructive size-8"
              onClick={() => this.setState({ pendingDelete: s })}
              aria-label={t("Xoá")}
              disabled={isBusy}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t("Xoá")}</TooltipContent>
        </Tooltip>
      </div>
    );
  }

  _renderGrid(items: CustomStyleDto[]) {
    const t = this.props.t;
    return (
      <div className={GRID}>
        {items.map((s) => {
          const isBusy = this.state.busyId === s.id;
          return (
            <Card
              key={s.id}
              role="button"
              tabIndex={0}
              aria-disabled={isBusy}
              onClick={() => !isBusy && this._handleOpen(s)}
              onKeyDown={(e) =>
                !isBusy && this._onCardKey(e, () => this._handleOpen(s))
              }
              className={cn(
                "group hover:border-primary/40 gap-0 overflow-hidden py-0 shadow-none transition-colors",
                isBusy && "pointer-events-none opacity-60"
              )}
            >
              <MapThumb
                styleUrl={this._buildStyleUrl(s)}
                themeMode={s.themeMode}
                className="aspect-video w-full border-b"
              />
              <CardContent className="flex items-start gap-2 px-4 py-3">
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <h3
                      className="truncate text-sm font-semibold"
                      title={s.name}
                    >
                      {s.name}
                    </h3>
                    {hasNewerDraft(s) && <DraftBadge at={s.draftDt!} by={s.draftBy} />}
                  </div>
                  <div className="text-muted-foreground flex items-center gap-1 text-xs">
                    <Clock className="h-3.5 w-3.5" />
                    {formatRelative(s.modifiedDt || s.createdDt, t)}
                  </div>
                </div>
                {this._renderRowActions(s)}
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  }

  _renderTable(items: CustomStyleDto[]) {
    const t = this.props.t;
    return (
      <div className="overflow-hidden rounded-md border">
        <Table>
          <TableHeader className="bg-[#F5F7F9] dark:bg-muted">
            <TableRow>
              <TableHead className="w-28 border-r">{t("Preview")}</TableHead>
              <TableHead className="border-r">{t("Name")}</TableHead>
              <TableHead className="w-28 border-r">{t("Theme")}</TableHead>
              <TableHead className="border-r">{t("Tags")}</TableHead>
              <TableHead className="w-36 border-r">{t("Last Updated")}</TableHead>
              <TableHead className="w-24 text-right">{t("Thao tác")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((s, i) => (
              <TableRow
                key={s.id}
                className={cn(
                  "hover:bg-muted/50 cursor-pointer",
                  i % 2 === 1 && "bg-gray-50 dark:bg-muted/20"
                )}
                onClick={() => this.state.busyId !== s.id && this._handleOpen(s)}
              >
                <TableCell className="border-r py-1.5">
                  <MapThumb
                    styleUrl={this._buildStyleUrl(s)}
                    themeMode={s.themeMode}
                    className="aspect-video w-20 rounded-sm border"
                  />
                </TableCell>
                <TableCell className="max-w-72 border-r">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <div className="truncate font-semibold text-primary dark:text-blue-400">
                      {s.name}
                    </div>
                    {hasNewerDraft(s) && <DraftBadge at={s.draftDt!} by={s.draftBy} />}
                  </div>
                  {s.description && (
                    <div className="text-muted-foreground truncate text-xs">
                      {s.description}
                    </div>
                  )}
                </TableCell>
                <TableCell className="border-r">
                  <Badge variant="outline">{themeLabel(s.themeMode, t)}</Badge>
                </TableCell>
                <TableCell className="border-r">
                  {s.tags && s.tags.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {s.tags.slice(0, 3).map((tag) => (
                        <Badge
                          key={tag}
                          className="bg-muted text-muted-foreground border-transparent"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground border-r">
                  {formatRelative(s.modifiedDt || s.createdDt, t)}
                </TableCell>
                <TableCell>
                  <div className="flex justify-end">{this._renderRowActions(s)}</div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  _renderMyMaps() {
    const { loading, loadError, viewMode } = this.state;
    const items = this._filteredItems();
    const t = this.props.t;

    let body: React.ReactNode;
    if (loading) {
      body = (
        <div className={GRID}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="aspect-video w-full rounded-xl" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ))}
        </div>
      );
    } else if (loadError) {
      body = <ErrorBanner message={loadError} />;
    } else if (items.length === 0) {
      body = (
        <div className="text-muted-foreground flex h-32 items-center justify-center rounded-xl border border-dashed text-sm">
          {this.state.keyword.trim()
            ? t("Không có style nào khớp từ khoá.")
            : t('Chưa có style nào. Bấm "Create New Map" để bắt đầu.')}
        </div>
      );
    } else {
      body = viewMode === "list" ? this._renderTable(items) : this._renderGrid(items);
    }

    return (
      <section className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-medium">{t("My Maps")}</h2>
          {!loading && !loadError && (
            <Badge variant="secondary">{t("{{n}} maps", { n: items.length })}</Badge>
          )}
          <div className="ms-auto flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="text-muted-foreground pointer-events-none absolute start-2.5 top-1/2 h-4 w-4 -translate-y-1/2" />
              <Input
                className="h-8 w-[200px] ps-8 lg:w-[260px]"
                placeholder={t("Tìm theo tên style…")}
                value={this.state.keyword}
                onChange={(e) => this.setState({ keyword: e.target.value })}
              />
            </div>
            <div className="flex items-center rounded-md border p-0.5">
              <ViewToggle
                active={viewMode === "grid"}
                label={t("Dạng lưới")}
                onClick={() => this.setState({ viewMode: "grid" })}
              >
                <LayoutGrid className="h-4 w-4" />
              </ViewToggle>
              <ViewToggle
                active={viewMode === "list"}
                label={t("Dạng danh sách")}
                onClick={() => this.setState({ viewMode: "list" })}
              >
                <List className="h-4 w-4" />
              </ViewToggle>
            </div>
          </div>
        </div>
        {body}
      </section>
    );
  }

  _renderVectorMaps() {
    const t = this.props.t;
    const apiKey = getStoredApiKey() || "";
    return (
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-medium">{t("Vector Maps")}</h2>
          <Badge variant="secondary">
            {t("{{n}} templates", { n: VECTOR_PRESETS.length })}
          </Badge>
        </div>
        <div className={GRID}>
          {VECTOR_PRESETS.map((p) => {
            const tmpl = VIETMAP_STYLE_URLS[p.styleId];
            const previewUrl = apiKey && tmpl ? tmpl.replace("{apikey}", apiKey) : "";
            return (
              <Card
                key={p.id}
                role="button"
                tabIndex={0}
                onClick={() => this._handleOpenPreset(p.styleId)}
                onKeyDown={(e) =>
                  this._onCardKey(e, () => this._handleOpenPreset(p.styleId))
                }
                className="hover:border-primary/40 gap-0 overflow-hidden py-0 shadow-none transition-colors"
              >
                <MapThumb
                  styleUrl={previewUrl}
                  themeMode={p.themeMode}
                  className="aspect-video w-full border-b"
                />
                <CardContent className="space-y-1.5 px-4 py-3">
                  <h3 className="text-sm font-semibold">{t(p.name)}</h3>
                  <p className="text-muted-foreground line-clamp-2 text-xs">
                    {t(p.description)}
                  </p>
                  <Badge
                    variant="outline"
                    className="text-muted-foreground font-normal"
                  >
                    {t(p.badge)}
                  </Badge>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>
    );
  }

  _renderSignedOut() {
    return <SignedOutLanding onOpenEditor={() => this.props.navigate("/create")} />;
  }

  render() {
    const t = this.props.t;
    const { totalPage, page, pendingDelete } = this.state;
    const crumbs = [{ label: t("Maps") }];

    // Unauthenticated state — block the page entirely until the user signs
    // in. Without this guard a fresh visitor would see styles from the default
    // fallback key (tokens.vietmap) which is a placeholder, not a real key.
    if (!getUser()) {
      return <AppShell crumbs={crumbs}>{this._renderSignedOut()}</AppShell>;
    }

    return (
      <AppShell crumbs={crumbs}>
        <div className="space-y-6">
          <PageHeader
            title={t("Maps Dashboard")}
            description={t("Manage and customise your map styles")}
          >
            <Button
              variant="outline"
              onClick={() => this.props.navigate("/compare")}
              title={t("So sánh 2 style cạnh nhau")}
            >
              <Columns2 className="h-4 w-4" />
              <span className="sr-only sm:not-sr-only">{t("So sánh")}</span>
            </Button>
            <Button onClick={() => this.props.navigate("/create")}>
              <Plus className="h-4 w-4" />
              <span className="sr-only sm:not-sr-only">{t("Create New Map")}</span>
            </Button>
          </PageHeader>

          {this._renderMyMaps()}
          <StyleTemplatesSection
            styleUrls={VIETMAP_STYLE_URLS}
            apiKey={getStoredApiKey() || ""}
            onOpen={(tpl) => this.props.navigate(`/create?template=${encodeURIComponent(tpl.id)}`)}
          />
          {this._renderVectorMaps()}

          {totalPage > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="size-8"
                onClick={() => this._goToPage(page - 1)}
                disabled={page <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-muted-foreground text-sm">
                {t("Trang {{p}} / {{total}}", { p: page, total: totalPage })}
              </span>
              <Button
                variant="outline"
                size="icon"
                className="size-8"
                onClick={() => this._goToPage(page + 1)}
                disabled={page >= totalPage}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        {this._shareModalMounted() && (
          <React.Suspense fallback={null}>
            <ShareEmbedModal
              open={!!this.state.shareFor}
              style={this.state.shareFor}
              apiKey={getStoredApiKey() || ""}
              consumerId={getConsumerId() || ""}
              initialTab={this.state.shareTab}
              onClose={() => this.setState({ shareFor: null })}
            />
          </React.Suspense>
        )}

        <ConfirmDialog
          destructive
          open={!!pendingDelete}
          onOpenChange={(open) => !open && this.setState({ pendingDelete: null })}
          title={t("Xoá style")}
          desc={t(
            'Xác nhận xoá style "{{name}}"? Hành động này có thể hoàn tác bằng cách liên hệ admin.',
            { name: pendingDelete?.name ?? "" }
          )}
          confirmText={t("Xoá")}
          cancelBtnText={t("Huỷ")}
          isLoading={!!this.state.busyId}
          handleConfirm={this._confirmDelete}
        />
      </AppShell>
    );
  }
}

function ViewToggle({
  active,
  label,
  onClick,
  children,
}: {
  active: boolean
  label: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn(
        "size-7",
        active && "bg-muted text-foreground",
        !active && "text-muted-foreground"
      )}
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      title={label}
    >
      {children}
    </Button>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-900/50 dark:bg-red-950/30">
      <div className="flex h-7 w-7 flex-none items-center justify-center rounded-md bg-red-500 text-white">
        <AlertTriangle className="h-4 w-4" />
      </div>
      <p className="self-center text-sm text-red-900 dark:text-red-200">
        {message}
      </p>
    </div>
  );
}

// Hoisted: wrapping inside the render function would create a new component
// type on every render and remount the whole page.
const ManagePageWithT = withTranslation()(ManagePageInternal);

const ManagePage: React.FC = () => {
  const navigate = useNavigate();
  return <ManagePageWithT navigate={navigate} />;
};

export default ManagePage;
